<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import emailMarketingProvidersAPI from 'dashboard/api/emailMarketingProviders';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();

const isLoading = ref(true);
const isSaving = ref(false);
const isTesting = ref(false);
const deleteDialogRef = ref(null);

const provider = ref(null);

const form = ref({
  from_email: '',
  from_name: '',
  active: true,
  settings: {
    region: '',
    access_key_id: '',
    secret_access_key: '',
  },
});

const testEmail = ref('');
const testDialogRef = ref(null);

const isConfigured = computed(() => provider.value !== null);

const statusLabel = computed(() =>
  provider.value?.active
    ? t('INTEGRATION_SETTINGS.AMAZON_SES.STATUS.ACTIVE')
    : t('INTEGRATION_SETTINGS.AMAZON_SES.STATUS.INACTIVE')
);

const loadProvider = async () => {
  isLoading.value = true;
  try {
    const { data } = await emailMarketingProvidersAPI.list();
    const ses = data.find(p => p.provider_type === 'aws_ses');
    if (ses) {
      provider.value = ses;
      form.value = {
        from_email: ses.from_email,
        from_name: ses.from_name,
        active: ses.active,
        settings: {
          region: ses.settings?.region || '',
          access_key_id: ses.settings?.access_key_id || '',
          secret_access_key: '',
        },
      };
    }
  } catch {
    // No provider configured yet
  } finally {
    isLoading.value = false;
  }
};

const saveProvider = async () => {
  isSaving.value = true;
  try {
    const payload = {
      provider_type: 'aws_ses',
      from_email: form.value.from_email,
      from_name: form.value.from_name,
      active: form.value.active,
      settings: { ...form.value.settings },
    };

    if (provider.value) {
      const { data } = await emailMarketingProvidersAPI.update(provider.value.id, payload);
      provider.value = data;
    } else {
      const { data } = await emailMarketingProvidersAPI.create(payload);
      provider.value = data;
    }

    useAlert(t('INTEGRATION_SETTINGS.AMAZON_SES.SAVE.SUCCESS'));
  } catch (e) {
    useAlert(t('INTEGRATION_SETTINGS.AMAZON_SES.SAVE.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const openTestDialog = () => {
  testEmail.value = '';
  testDialogRef.value?.open();
};

const sendTestEmail = async () => {
  if (!testEmail.value) return;
  isTesting.value = true;
  try {
    await emailMarketingProvidersAPI.test(provider.value.id, testEmail.value);
    useAlert(t('INTEGRATION_SETTINGS.AMAZON_SES.TEST.SUCCESS'));
    testDialogRef.value?.close();
  } catch {
    useAlert(t('INTEGRATION_SETTINGS.AMAZON_SES.TEST.ERROR'));
  } finally {
    isTesting.value = false;
  }
};

const openDeleteDialog = () => {
  deleteDialogRef.value?.open();
};

const deleteProvider = async () => {
  try {
    await emailMarketingProvidersAPI.delete(provider.value.id);
    provider.value = null;
    form.value = {
      from_email: '',
      from_name: '',
      active: true,
      settings: { region: '', access_key_id: '', secret_access_key: '' },
    };
    useAlert(t('INTEGRATION_SETTINGS.AMAZON_SES.DELETE.SUCCESS'));
  } catch {
    useAlert(t('INTEGRATION_SETTINGS.AMAZON_SES.DELETE.ERROR'));
  }
};

onMounted(loadProvider);
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.AMAZON_SES.HEADER')"
        :description="$t('INTEGRATION_SETTINGS.AMAZON_SES.DESCRIPTION')"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>

    <template #body>
      <div class="flex flex-col gap-6 max-w-2xl">

        <!-- Status banner when configured -->
        <div
          v-if="isConfigured"
          class="flex items-center justify-between p-4 rounded-xl outline outline-1 outline-n-container bg-n-card"
        >
          <div class="flex items-center gap-3">
            <img
              :src="'/dashboard/images/integrations/amazon_ses.png'"
              class="w-10 h-10 rounded-md block dark:hidden"
            />
            <img
              :src="'/dashboard/images/integrations/amazon_ses-dark.png'"
              class="w-10 h-10 rounded-md hidden dark:block"
            />
            <div>
              <p class="text-heading-3 text-n-slate-12 font-medium">Amazon SES</p>
              <p class="text-body-sm text-n-slate-11">
                {{ statusLabel }} · {{ provider.from_email }}
              </p>
            </div>
          </div>
          <div class="flex gap-2">
            <Button
              :label="$t('INTEGRATION_SETTINGS.AMAZON_SES.TEST.BUTTON')"
              icon="i-lucide-send"
              slate
              xs
              @click="openTestDialog"
            />
            <Button
              :label="$t('INTEGRATION_SETTINGS.AMAZON_SES.DELETE.CONFIRM')"
              icon="i-lucide-trash-2"
              ruby
              xs
              @click="openDeleteDialog"
            />
          </div>
        </div>

        <!-- Configuration form -->
        <div class="p-6 rounded-xl outline outline-1 outline-n-container bg-n-card flex flex-col gap-4">
          <p class="text-heading-3 text-n-slate-12 font-medium">
            {{ $t('INTEGRATION_SETTINGS.AMAZON_SES.FORM.TITLE') }}
          </p>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              v-model="form.from_email"
              :label="$t('INTEGRATION_SETTINGS.AMAZON_SES.FORM.FROM_EMAIL_LABEL')"
              :placeholder="$t('INTEGRATION_SETTINGS.AMAZON_SES.FORM.FROM_EMAIL_PLACEHOLDER')"
            />
            <Input
              v-model="form.from_name"
              :label="$t('INTEGRATION_SETTINGS.AMAZON_SES.FORM.FROM_NAME_LABEL')"
              :placeholder="$t('INTEGRATION_SETTINGS.AMAZON_SES.FORM.FROM_NAME_PLACEHOLDER')"
            />
          </div>

          <div class="border-t border-n-weak pt-4 flex flex-col gap-4">
            <p class="text-body-sm text-n-slate-11 font-medium uppercase tracking-wider">AWS Credentials</p>
            <Input
              v-model="form.settings.region"
              :label="$t('INTEGRATION_SETTINGS.AMAZON_SES.FORM.REGION_LABEL')"
              :placeholder="$t('INTEGRATION_SETTINGS.AMAZON_SES.FORM.REGION_PLACEHOLDER')"
            />
            <Input
              v-model="form.settings.access_key_id"
              :label="$t('INTEGRATION_SETTINGS.AMAZON_SES.FORM.ACCESS_KEY_LABEL')"
              :placeholder="$t('INTEGRATION_SETTINGS.AMAZON_SES.FORM.ACCESS_KEY_PLACEHOLDER')"
            />
            <Input
              v-model="form.settings.secret_access_key"
              type="password"
              :label="$t('INTEGRATION_SETTINGS.AMAZON_SES.FORM.SECRET_KEY_LABEL')"
              :placeholder="isConfigured ? '••••••••' : $t('INTEGRATION_SETTINGS.AMAZON_SES.FORM.SECRET_KEY_PLACEHOLDER')"
            />
          </div>

          <div class="flex items-center gap-3 pt-2">
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="form.active"
                type="checkbox"
                class="w-4 h-4 rounded accent-woot-500"
              />
              <span class="text-body-main text-n-slate-12">
                {{ $t('INTEGRATION_SETTINGS.AMAZON_SES.FORM.ACTIVE_LABEL') }}
              </span>
            </label>
          </div>

          <div class="flex justify-end gap-2 pt-2">
            <Button
              :label="$t('INTEGRATION_SETTINGS.AMAZON_SES.FORM.SAVE')"
              teal
              :is-loading="isSaving"
              @click="saveProvider"
            />
          </div>
        </div>
      </div>

      <!-- Test email dialog -->
      <Dialog
        ref="testDialogRef"
        :title="$t('INTEGRATION_SETTINGS.AMAZON_SES.TEST.TITLE')"
        :is-loading="isTesting"
        @confirm="sendTestEmail"
      >
        <Input
          v-model="testEmail"
          type="email"
          :label="$t('INTEGRATION_SETTINGS.AMAZON_SES.TEST.EMAIL_LABEL')"
          :placeholder="$t('INTEGRATION_SETTINGS.AMAZON_SES.TEST.EMAIL_PLACEHOLDER')"
        />
      </Dialog>

      <!-- Delete confirmation dialog -->
      <Dialog
        ref="deleteDialogRef"
        :title="$t('INTEGRATION_SETTINGS.AMAZON_SES.DELETE.TITLE')"
        :message="$t('INTEGRATION_SETTINGS.AMAZON_SES.DELETE.MESSAGE')"
        :confirm-text="$t('INTEGRATION_SETTINGS.AMAZON_SES.DELETE.CONFIRM')"
        confirm-type="ruby"
        @confirm="deleteProvider"
      />
    </template>
  </SettingsLayout>
</template>
